import { useState, type FormEvent } from 'react';
import Container from '~/components/Container';
import Button from '~/components/Button';
import Reveal from '~/components/Reveal';
import { apiFind, apiGet, apiSearch, sendContact } from '~/lib/api';
import { useTitle } from '~/lib/useTitle';
import s from './Docs.module.scss';

const LIMITS = [5, 10, 15, 20, 30, 40, 50];

type Result = { url: string; loading: boolean; data: unknown; error?: string };

async function run(url: string, fn: () => Promise<unknown>, set: (r: Result) => void) {
  set({ url, loading: true, data: null });
  try {
    set({ url, loading: false, data: await fn() });
  } catch (e) {
    set({ url, loading: false, data: null, error: (e as Error).message || 'Request failed' });
  }
}

function ResultView({ result }: { result: Result | null }) {
  if (!result) return null;
  return (
    <div className={s.result}>
      <div className={s.reqUrl}>
        <span>GET</span> {result.url}
      </div>
      {result.loading ? (
        <div className={s.muted}>Running…</div>
      ) : result.error ? (
        <div className={s.error}>{result.error}</div>
      ) : (
        <pre className={s.json}>{JSON.stringify(result.data, null, 2)}</pre>
      )}
    </div>
  );
}

function FindPanel() {
  const [type, setType] = useState<'movie' | 'series'>('movie');
  const [limit, setLimit] = useState(10);
  const [res, setRes] = useState<Result | null>(null);

  return (
    <div className={s.panel}>
      <div className={s.panelHead}>
        <h3>Popular titles</h3>
        <p>Top movies or series straight from the catalog.</p>
      </div>
      <div className={s.controls}>
        <label>
          Type
          <select value={type} onChange={(e) => setType(e.target.value as 'movie' | 'series')}>
            <option value="movie">Movie</option>
            <option value="series">Series</option>
          </select>
        </label>
        <label>
          Limit
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            {LIMITS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <Button onClick={() => run(`/api/find?type=${type}&limit=${limit}`, () => apiFind(type, limit), setRes)}>Run</Button>
      </div>
      <ResultView result={res} />
    </div>
  );
}

function SearchPanel() {
  const [q, setQ] = useState('superman');
  const [res, setRes] = useState<Result | null>(null);
  const go = () => {
    if (!q.trim()) return;
    run(`/api/search?q=${encodeURIComponent(q)}`, () => apiSearch(q), setRes);
  };

  return (
    <div className={s.panel}>
      <div className={s.panelHead}>
        <h3>Search</h3>
        <p>Find movies and series by title.</p>
      </div>
      <div className={s.controls}>
        <input
          value={q}
          placeholder="Search query"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && go()}
        />
        <Button onClick={go}>Run</Button>
      </div>
      <ResultView result={res} />
    </div>
  );
}

function GetPanel() {
  const [imdb, setImdb] = useState('tt0944947');
  const [res, setRes] = useState<Result | null>(null);
  const go = () => {
    if (!imdb.trim()) return;
    run(`/api/get?imdb_id=${encodeURIComponent(imdb)}`, () => apiGet(imdb), setRes);
  };

  return (
    <div className={s.panel}>
      <div className={s.panelHead}>
        <h3>Item metadata</h3>
        <p>Fetch full metadata for a single title by IMDB id.</p>
      </div>
      <div className={s.controls}>
        <input
          value={imdb}
          placeholder="IMDB id, e.g. tt0944947"
          onChange={(e) => setImdb(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && go()}
        />
        <Button onClick={go}>Run</Button>
      </div>
      <ResultView result={res} />
    </div>
  );
}

function Contact() {
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<{ header: string; text: string; ok: boolean } | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setSending(true);
    setMsg(null);
    try {
      const r = await sendContact(
        String(data.get('name') || ''),
        String(data.get('mail') || ''),
        String(data.get('msg') || ''),
      );
      if (r.contact?.success) {
        setMsg({ ...r.contact.success, ok: true });
        form.reset();
      } else if (r.contact?.err) {
        setMsg({ ...r.contact.err, ok: false });
      }
    } catch {
      setMsg({ header: 'Error', text: 'Something went wrong. Please try again.', ok: false });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={s.contact} id="contact">
      <div className={s.contactCopy}>
        <div className={s.kicker}>Contact</div>
        <h2>More questions?</h2>
        <p>Tell us about your project and we’ll get back to you.</p>
      </div>
      <form className={s.form} onSubmit={onSubmit}>
        <input name="name" placeholder="Name" required />
        <input name="mail" type="email" placeholder="E-mail" required />
        <textarea name="msg" rows={5} placeholder="Message" required />
        {msg && <div className={msg.ok ? s.formOk : s.formErr}>{msg.text}</div>}
        <Button type="submit" className={s.submit}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </form>
    </div>
  );
}

export default function Docs() {
  useTitle('API Docs — MetaHub');

  return (
    <>
      <section className={s.head}>
        <Container>
          <Reveal>
            <div className={s.kicker}>API Playground</div>
            <h1>Try the MetaHub API.</h1>
            <p>Run live requests against the metadata API right from your browser — no key required for the demo.</p>
            <Button to="/docs/api" variant="ghost" className={s.refLink}>
              Read the API reference
            </Button>
          </Reveal>
        </Container>
      </section>

      <section className={s.panels}>
        <Container>
          <Reveal>
            <FindPanel />
          </Reveal>
          <Reveal>
            <SearchPanel />
          </Reveal>
          <Reveal>
            <GetPanel />
          </Reveal>
        </Container>
      </section>

      <section className={s.contactSection}>
        <Container>
          <Reveal>
            <Contact />
          </Reveal>
        </Container>
      </section>
    </>
  );
}
